import { Component, ElementRef, HostBinding, OnChanges, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ContentChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

/**
 * Represents the title of the dropdown. This component is used to project content as the dropdown's title.
 */
@Component({
    selector: 'upp-dropdown-title',
    template: `
        <ion-item lines="none">
            <ion-label>
                <ng-content></ng-content>
            </ion-label>
        </ion-item>
    `,
    styleUrls: [ './upp-dropdown.scss' ]
})
export class UppDropdownTitleComponent {
    // nothing to do
}

/**
 * Represents a content section in the dropdown. This component is used to project content
 * as part of the dropdown menu options.
 */
@Component({
    selector: 'upp-dropdown-content',
    template: `<ng-content></ng-content>`,
    styleUrls: [ './upp-dropdown.scss' ]
})
export class UppDropdownContentComponent {
    // nothing to do
}

/**
 * Represents a visual separator in the dropdown. This component is used to render a styled line
 * separating sections within the dropdown menu.
 */
@Component({
    selector: 'upp-dropdown-separate',
    template: `<div style="clear:both; height: 1px; margin: 8px 0px; border-bottom: 5px solid var(--ion-color-light)"></div>`,
    styleUrls: [],
})
export class UppDropdownSplitComponent {
    // nothing to do
}

/**
 * Main dropdown component that handles the projection and rendering of dropdown content, separators, and title.
 * The component preserves the order of the child elements and dynamically renders them.
 */
@Component({
    selector: 'upp-dropdown',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-dropdown.html',
    styleUrls: ['./upp-dropdown.scss']
})
export class UppDropdownComponent implements OnChanges {
    /** Indicates whether the dropdown is expanded or collapsed. */
    @Input() expand = false;
    /**
     * Visual fill of the panel: `solid` (default) matches the light panel shell;
     * `clear` uses a transparent shell and bordered list (see `.dropdown.clear` in styles).
     */
    @Input() fill: 'solid' | 'clear' = 'solid';
    /** Emits an event when the dropdown is closed. */
    @Output() Closed = new EventEmitter<void>();

    /**
     * @param change ChangeDetectorRef for triggering manual change detection.
     * @param hostRef Host element (used to clamp the floating panel inside the viewport).
     */
    constructor(
        private change: ChangeDetectorRef,
        private hostRef: ElementRef<HTMLElement>,
    ){
        // nothing to do
    }

    @ContentChild(UppDropdownTitleComponent) _title: UppDropdownTitleComponent | null = null;
    get HasTitle(): boolean {
        return !!this._title;
    }

    /** Gap kept between the panel bottom and the viewport edge. */
    private static readonly _PANEL_GAP = 8;
    /** Usable height floor: below it the menu is unreadable, so it overflows the edge instead. */
    private static readonly _PANEL_MIN = 120;

    private _maxheight: string | null = null;

    /**
     * Height available for the panel, measured when it opens. The sheet owns what the
     * value means; `null` leaves the panel at its sheet-defined size.
     */
    @HostBinding('style.--upp-dropdown-panel-max-height')
    get MaxHeight(): string | null {
        return this._maxheight;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['expand']){
            if (this.expand){
                queueMicrotask(() => this.FitToViewport());
            }
            else {
                this._maxheight = null;
            }
        }
    }

    /**
     * Closes the dropdown and triggers the `OnClosed` event.
     */
    OnCloseDropdown() : void {
        this.expand = false;
        this._maxheight = null;
        this.change.markForCheck();
        this.Closed.next();
    }

    /**
     * Caps panel height so an anchored menu opened near the bottom stays inside the viewport.
     */
    private FitToViewport(): void {
        const host = this.hostRef.nativeElement;
        const panel = host.querySelector('.dropdown') as HTMLElement | null;
        if (!panel){
            return;
        }

        const anchor = panel.parentElement;
        const top = (anchor?.getBoundingClientRect().bottom ?? panel.getBoundingClientRect().top);
        const remaining = window.innerHeight - top - UppDropdownComponent._PANEL_GAP;
        const ceiling = Math.floor(window.innerHeight * 0.5);
        const capped = Math.max(Math.min(remaining, ceiling), UppDropdownComponent._PANEL_MIN);

        this._maxheight = `${capped}px`;
        this.change.markForCheck();
    }
}
